<div class="dossier-list-container">
  <div class="header">
    <h2>{{ 'dossier.title' | translate }}</h2>
    <div class="header-actions" *ngIf="!showCreateForm">
      <button class="btn-icon" (click)="loadDossiers()" [title]="'dossier.refresh' | translate" aria-label="Refresh dossiers">
        ↻
      </button>
      <button class="btn-primary" (click)="openCreateForm()">
        + {{ 'dossier.newDossier' | translate }}
      </button>
    </div>
  </div>

  <!-- Create form -->
  <div class="create-form" *ngIf="showCreateForm">
    <div class="form-group">
      <label for="externalId">{{ 'dossier.externalId' | translate }}</label>
      <input
        id="externalId"
        type="text"
        [(ngModel)]="newExternalId"
        [placeholder]="'dossier.externalId' | translate"
        required
      />
    </div>

    <!-- Processing Mode with help icon -->
    <div class="form-group">
      <label for="processingMode">
        {{ 'dossier.processingMode' | translate }}
        <button
          type="button"
          class="help-icon"
          (click)="openHelpDialog()"
          [title]="'dossier.processingModeHelp' | translate"
          aria-label="Processing mode help"
        >ⓘ</button>
      </label>
      <select id="processingMode" [(ngModel)]="newProcessingMode">
        <option value="manual">{{ 'dossier.manual' | translate }}</option>
        <option value="auto">{{ 'dossier.auto' | translate }}</option>
      </select>
    </div>

    <!-- Batch Mode checkbox -->
    <div class="form-group form-group-inline">
      <label class="checkbox-label">
        <input
          type="checkbox"
          [(ngModel)]="newBatchMode"
          (ngModelChange)="onBatchModeChange()"
        />
        {{ 'dossier.batchMode' | translate }}
      </label>
    </div>

    <!-- Model selection (visible and mandatory when batch mode is on) -->
    <div class="batch-model-section" *ngIf="newBatchMode">
      <!-- Extraction backend selector -->
      <div class="form-group">
        <label for="batchBackend">{{ 'dossier.batchBackend' | translate }} *</label>
        <select id="batchBackend" [(ngModel)]="newBatchBackend" (ngModelChange)="onBatchBackendChange()" required>
          <option value="" disabled>-- {{ 'dossier.batchBackend' | translate }} --</option>
          <option *ngFor="let b of availableBackends" [value]="b">{{ displayBackend(b) }}</option>
        </select>
      </div>

      <!-- Extraction model selector (filtered by backend, free-text editable) -->
      <div class="form-group">
        <label for="batchModel">{{ 'dossier.batchModel' | translate }} *</label>
        <input id="batchModel" list="batch-model-options" [(ngModel)]="newBatchModel" required placeholder="Select or type model name">
        <datalist id="batch-model-options">
          <option *ngFor="let m of filteredBatchModels" [value]="m">{{ displayModel(m) }} {{ modelPriceLabel(m) }}</option>
        </datalist>
        <span class="field-hint" *ngIf="!newBatchModel.trim()">
          {{ 'dossier.batchModelRequired' | translate }}
        </span>
      </div>

      <!-- Schema source selector (internal / external) -->
      <div class="form-group">
        <label for="batchSchemaSource">{{ 'dossier.batchSchemaSource' | translate }}</label>
        <select id="batchSchemaSource" [(ngModel)]="newBatchSchemaSource" (ngModelChange)="onBatchSchemaSourceChange()">
          <option value="internal">{{ 'schemaSourceOptions.internal' | translate }}</option>
          <option value="external">{{ 'schemaSourceOptions.external' | translate }}</option>
        </select>
      </div>

      <!-- Document type selector (filtered by schema source) -->
      <div class="form-group">
        <label for="batchDocumentType">{{ 'dossier.batchDocumentType' | translate }}</label>
        <select id="batchDocumentType" [(ngModel)]="newBatchDocumentType">
          <option value="">{{ 'dossier.batchDocumentTypeAuto' | translate }}</option>
          <option *ngFor="let dt of filteredBatchDocumentTypes" [value]="dt">{{ dt }}</option>
        </select>
        <span class="field-hint">{{ 'dossier.batchDocumentTypeHint' | translate }}</span>
      </div>

      <!-- Output format selector -->
      <div class="form-group">
        <label for="batchOutputFormat">{{ 'dossier.batchOutputFormat' | translate }}</label>
        <select id="batchOutputFormat" [(ngModel)]="newBatchOutputFormat">
          <option *ngFor="let fmt of availableOutputFormats" [value]="fmt">{{ fmt | uppercase }}</option>
        </select>
      </div>
    </div>

    <div class="form-actions">
      <button class="btn-primary" (click)="createDossier()" [disabled]="!canCreate">
        {{ 'dossier.create' | translate }}
      </button>
      <button class="btn-secondary" (click)="cancelCreate()" [disabled]="creating">
        {{ 'dossier.cancel' | translate }}
      </button>
    </div>
  </div>

  <!-- Help dialog (modal overlay) -->
  <div class="modal-overlay" *ngIf="showHelpDialog" (click)="closeHelpDialog()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>{{ 'dossier.processingModeHelp' | translate }}</h3>
        <button class="modal-close" (click)="closeHelpDialog()" aria-label="Close">✕</button>
      </div>
      <div class="modal-body">
        <div class="help-item">
          <span class="help-label">Manual</span>
          <p>{{ 'dossier.helpManual' | translate }}</p>
        </div>
        <div class="help-item">
          <span class="help-label">Auto</span>
          <p>{{ 'dossier.helpAuto' | translate }}</p>
        </div>
        <div class="help-item">
          <span class="help-label">Batch</span>
          <p>{{ 'dossier.helpBatch' | translate }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Keypair generation dialog -->
  <div class="modal-overlay" *ngIf="showKeypairDialog">
    <div class="modal-content keypair-dialog">
      <div class="modal-header">
        <h3>🔑 {{ 'dossier.keypairDialogTitle' | translate }}</h3>
      </div>
      <div class="modal-body">
        <p class="keypair-hint">{{ 'dossier.keypairDialogHint' | translate }}</p>

        <div class="sec-form">
          <label>{{ 'dossier.keypairPasswordLabel' | translate }}</label>
          <div class="password-field">
            <input
              [type]="showKeypairPassword ? 'text' : 'password'"
              [(ngModel)]="keypairPassword"
              [placeholder]="'dossier.keypairPasswordPlaceholder' | translate"
              (keyup.enter)="submitKeypair()"
              [disabled]="keypairGenerating"
            />
            <button type="button" class="eye-btn" (click)="showKeypairPassword = !showKeypairPassword"
                    [title]="showKeypairPassword ? ('dossier.hidePassword' | translate) : ('dossier.showPassword' | translate)">
              {{ showKeypairPassword ? '🙈' : '👁' }}
            </button>
          </div>

          <label style="margin-top:12px">{{ 'dossier.keypairConfirmLabel' | translate }}</label>
          <div class="password-field">
            <input
              [type]="showKeypairConfirm ? 'text' : 'password'"
              [(ngModel)]="keypairConfirm"
              [placeholder]="'dossier.keypairConfirmPlaceholder' | translate"
              (keyup.enter)="submitKeypair()"
              [disabled]="keypairGenerating"
            />
            <button type="button" class="eye-btn" (click)="showKeypairConfirm = !showKeypairConfirm"
                    [title]="showKeypairConfirm ? ('dossier.hidePassword' | translate) : ('dossier.showPassword' | translate)">
              {{ showKeypairConfirm ? '🙈' : '👁' }}
            </button>
          </div>

          <p class="keypair-warning">⚠ {{ 'dossier.keypairWarning' | translate }}</p>

          <div class="keypair-error" *ngIf="keypairError">{{ keypairError }}</div>

          <div class="keypair-actions">
            <button class="btn-primary" (click)="submitKeypair()" [disabled]="keypairGenerating || !keypairPassword || !keypairConfirm">
              <span *ngIf="!keypairGenerating">{{ 'dossier.keypairGenerate' | translate }}</span>
              <span *ngIf="keypairGenerating">{{ 'dossier.keypairGenerating' | translate }}</span>
            </button>
            <button class="btn-secondary" (click)="cancelKeypairDialog()" [disabled]="keypairGenerating">
              {{ 'dossier.cancel' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Error -->
  <div class="error-message" *ngIf="error">{{ error }}</div>

  <!-- Loading -->
  <div class="loading" *ngIf="loading">{{ 'dossier.loading' | translate }}</div>

  <!-- Filter bar -->
  <div class="filter-bar" *ngIf="!showCreateForm">
    <input
      type="text"
      class="filter-ref"
      [(ngModel)]="filterRef"
      (ngModelChange)="onFilterInputChange()"
      [placeholder]="'dossier.filterRef' | translate"
      [attr.aria-label]="'dossier.filterRef' | translate"
    />
    <select
      class="filter-status"
      [(ngModel)]="filterStatus"
      (change)="applyFilters()"
      [attr.aria-label]="'dossier.filterStatus' | translate"
    >
      <option value="">{{ 'dossier.filterStatusAny' | translate }}</option>
      <option value="created">{{ 'dossier.filterStatusCreated' | translate }}</option>
      <option value="processing">{{ 'dossier.filterStatusProcessing' | translate }}</option>
      <option value="completed">{{ 'dossier.filterStatusCompleted' | translate }}</option>
    </select>
    <label class="filter-date-label">
      {{ 'dossier.filterFrom' | translate }}
      <input type="date" [(ngModel)]="filterDateFrom" (change)="applyFilters()" />
    </label>
    <label class="filter-date-label">
      {{ 'dossier.filterTo' | translate }}
      <input type="date" [(ngModel)]="filterDateTo" (change)="applyFilters()" />
    </label>
  </div>

  <!-- Empty state -->
  <div class="empty-state" *ngIf="!loading && dossiers.length === 0 && !error">
    {{ 'dossier.empty' | translate }}
  </div>

  <!-- Dossier table -->
  <table class="dossier-table" *ngIf="!loading && dossiers.length > 0">
    <thead>
      <tr>
        <th class="sortable" (click)="setSort('external_id')">
          {{ 'dossier.externalId' | translate }}
          <span class="sort-arrow" *ngIf="sortBy === 'external_id'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
        </th>
        <th class="sortable" (click)="setSort('status')">
          {{ 'dossier.status' | translate }}
          <span class="sort-arrow" *ngIf="sortBy === 'status'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
        </th>
        <th>{{ 'dossier.uploads' | translate }}</th>
        <th class="sortable" (click)="setSort('created_at')">
          {{ 'dossier.created' | translate }}
          <span class="sort-arrow" *ngIf="sortBy === 'created_at'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
        </th>
        <th>{{ 'dossier.updated' | translate }}</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let d of dossiers" (click)="navigateToDossier(d)" class="clickable-row">
        <td>
          {{ d.external_id }}
          <span class="batch-badge" *ngIf="d.batch_mode">BATCH</span>
          <span class="encrypted-badge" *ngIf="d.dek_salt" title="{{ 'dossier.encrypted' | translate }}">🔒</span>
        </td>
        <td>
          <span class="status-badge" [style.background-color]="getStatusColor(d.status)">
            {{ d.status }}
          </span>
        </td>
        <td>{{ d.upload_count ?? 0 }}</td>
        <td>{{ d.created_at | localDateTime }}</td>
        <td>{{ d.updated_at | localDateTime }}</td>
        <td>
          <button
            class="btn-delete"
            (click)="deleteDossier($event, d)"
            [disabled]="isProcessing(d)"
            [title]="isProcessing(d) ? ('dossier.deleteDisabled' | translate) : ('dossier.deleteConfirm' | translate)"
          >
            ✕
          </button>
        </td>
      </tr>
    </tbody>
  </table>

  <div class="pagination" *ngIf="!loading && dossiers.length > 0">
    <span class="pagination-summary">
      {{ 'dossier.pageSummary' | translate: { page: pageNumber, pages: pageCount, total: total } }}
    </span>
    <button class="btn-icon" [disabled]="!hasPrevPage" (click)="prevPage()" [attr.aria-label]="'dossier.prevPage' | translate">‹</button>
    <button class="btn-icon" [disabled]="!hasNextPage" (click)="nextPage()" [attr.aria-label]="'dossier.nextPage' | translate">›</button>
  </div>
</div>
